import { Ajax } from "../Common/Ajax";
import { GlobalData } from "../Common/GlobalData";
import { AutoCompleteHelper, GridHelper, Notification, ReportDateHelper, TemplateHelpers } from "../Common/KendoHelpers";

export class ServerBuySellLists {
    public activities: string[] = [];
    public accounts: string[] = [];
}

export class BuySellLists {
    public activities: kendo.data.DataSource | null = null;
    public accounts: kendo.data.DataSource | null = null;
}

export class StocksToBuySellUpdate {
    private static readonly GRID = '#grid';

    private serverBuySellLists: ServerBuySellLists | null = null;
    private buySellLists: BuySellLists | null = null;
    private grid!: kendo.ui.Grid;

    private static _instance: StocksToBuySellUpdate;

    private constructor() {
        this.init();
    }

    public static getInstance() {
        return this._instance || (this._instance = new this());
    }

    // Can we show the Report Date on the top row or same row as the Title?
    private init() {
        this.initListValues();

        this.grid = $(StocksToBuySellUpdate.GRID).kendoGrid({
            dataSource: {
                transport: {
                    read: GlobalData.URL_BASE +
                        GlobalData.URL_SUFFIX_PERSONAL_REPORTS_STOCKS_TO_BUY_SELL_UPDATE,
                    create: {
                        url: GlobalData.URL_BASE +
                            GlobalData.URL_SUFFIX_PERSONAL_REPORTS_STOCKS_TO_BUY_SELL_UPDATE,
                        type: 'POST',
                        contentType: 'application/json',
                        dataType: 'json',
                    },
                    update: {
                        url: GlobalData.URL_BASE +
                            GlobalData.URL_SUFFIX_PERSONAL_REPORTS_STOCKS_TO_BUY_SELL_UPDATE,
                        type: 'PUT',
                        contentType: 'application/json',
                        dataType: 'json',
                    },

                    parameterMap: function (options: any, operation: any) {
                        if (operation !== "read" && options.models) {
                            return kendo.stringify(options.models);
                        }
                    }
                },
                batch: true,
                serverPaging: false,
                serverSorting: false,
                schema: {
                    model: {
                        id: "id",
                        fields: {
                            id: { type: "number" },
                            symbol: { type: "string", nullable: false },
                            close: { editable: false },
                            pivotPoint: { editable: false },
                            activity: {
                                type: "string",
                                validation: {
                                    custom: (input: any) => {
                                        // Workaround to deal with the fact that the autocomplete
                                        // sets the value to "[Object object]" after the first edit.
                                        const row = input.closest("tr");
                                        const dataItem = this.grid.dataItem(row) as any;
                                        dataItem.activity = input["0"].value;

                                        return true;
                                    }
                                }
                            },
                            quantity: { editable: true },
                            price: { editable: true },
                            priceDiff: { editable: false },
                            account: {
                                type: "string",
                                validation: {
                                    custom: (input: any) => {
                                        // Workaround to deal with the fact that the autocomplete
                                        // sets the value to "[Object object]" after the first edit.
                                        const row = input.closest("tr");
                                        const dataItem = this.grid.dataItem(row) as any;
                                        dataItem.account = input["0"].value;

                                        return true;
                                    }
                                }
                            },
                            sumQtyOwn: { editable: false },
                            costPerShare: { editable: false },
                            fullCurrentPctK: { editable: false },
                            pivotNote1: { editable: false },
                        }
                    }
                }
            },
            dataBound: () => {
                const data = $(StocksToBuySellUpdate.GRID).data('kendoGrid')!.dataSource.data();
                if (data && data.length > 0) {
                    ReportDateHelper.showReportDate(data[0].closingDate);
                }
                this.defineDeleteButtonEvents();
            },

            height: 915,
            groupable: false,
            editable: true,
            navigatable: true,
            sortable: true,
            filterable: false,
            pageable: false,
            resizable: true,
            toolbar: [
                { name: "create" },
                { name: "save" },
                { name: "cancel" },
            ],
            saveChanges: (e) => {
                const self = StocksToBuySellUpdate.getInstance();
                const rows = (this.grid as any)._data;
                const invalidRows = [];

                if (!self.serverBuySellLists) {
                    return;
                }

                // Check all of the global validation rules.
                for (let i = 0; i < rows.length; ++i) {
                    rows[i].validationErrorMessage = '';
                    if (rows[i].dirty) {
                        let invalid = false;

                        // Validate the activity value.
                        if (rows[i].activity == 'B' || rows[i].activity == 'b') {
                            rows[i].activity = 'Buy';
                        } else if (rows[i].activity == 'S' || rows[i].activity == 's') {
                            rows[i].activity = 'Sell';
                        }
                        if (self.serverBuySellLists.activities.indexOf(rows[i].activity) == -1) {
                            invalid = true;
                            rows[i].validationErrorMessage += rows[i].symbol +
                                ' activity is not valid: ' + rows[i].activity + '<br>';
                        }

                        // Validate the account value.
                        const account = rows[i].account;
                        for (const serverAccount of self.serverBuySellLists.accounts) {
                            if (serverAccount.slice(-2) == account) {
                                rows[i].account = serverAccount;
                            }
                        }

                        if (self.serverBuySellLists.accounts.indexOf(rows[i].account) == -1) {
                            invalid = true;
                            rows[i].validationErrorMessage += rows[i].symbol +
                                ' account is not valid: ' + rows[i].account + '<br>';
                        }

                        if (invalid) {
                            invalidRows.push(rows[i]);
                        }
                    }
                }

                // If there were any invalid rows, prevent the update and display the error messages.
                if (invalidRows.length > 0) {
                    // Don't call the server with the update.
                    e.preventDefault();

                    // Display all of the error messages.
                    let globalValidationErrorMessages = '';
                    for (let i = 0; i < invalidRows.length; ++i) {
                        globalValidationErrorMessages += invalidRows[i].validationErrorMessage;
                    }
                    $('#globalValidationErrorMessages').html(globalValidationErrorMessages);
                }
                else {
                    $('#globalValidationErrorMessages').html('');
                }
            },
            columns: [
            {
                field: 'symbol',
                title: 'Symbol',
                editor: this.symbolEditor,
                width: '7%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { "class": "table-cell", style: "text-align: center; font-size: 11px" },
            },
            {
                field: 'close',
                title: 'Close',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { "class": "table-cell", style: "text-align: center; font-size: 11px" },
                format: '{0:c2}',
            },
            {
                field: 'pivotPoint',
                title: 'Pivot<br/>Point',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { "class": "table-cell", style: "text-align: center; font-size: 11px" },
                format: '{0:c2}',
            },
            {
                field: 'activity',
                title: 'Buy<br/>Sell',
                editor: this.activityEditor,
                width: '5%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { "class": "table-cell", style: "text-align: center; font-size: 11px" },
            },
            {
                field: 'quantity',
                title: 'Buy<br/>Qty',
                width: '5%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { "class": "table-cell", style: "text-align: center; font-size: 11px" },
                format: '{0:n0}',
            },
            {
                field: 'price',
                title: 'Buy<br/>Price',
                editor: this.priceEditor,
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { "class": "table-cell", style: "text-align: center; font-size: 11px" },
                format: '{0:c2}',
            },
            {
                field: 'priceDiff',
                title: 'Price<br/>Diff',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { "class": "table-cell", style: "text-align: center; font-size: 11px" },
                template: kendo.template(TemplateHelpers.getColorCurrencyTemplate('priceDiff')),
            },
            {
                field: 'account',
                title: 'Account',
                editor: this.accountEditor,
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                //attributes: { 'class': 'table-cell grid-cell-center' },
                attributes: { "class": "table-cell", style: "text-align: center; font-size: 10px" },
            },
            {
                field: 'sumQtyOwn',
                title: 'Own<br/>Qty',
                width: '5%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { "class": "table-cell", style: "text-align: center; font-size: 11px" },
                format: '{0:n0}',
            },
            {
                field: 'costPerShare',
                title: 'Cost/<br/>Share',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { "class": "table-cell", style: "text-align: center; font-size: 11px" },
                format: '{0:c2}',
            },
            {
                field: 'fullCurrentPctK',
                title: 'Full<br/>%K',
                width: '6%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { "class": "table-cell", style: "text-align: center; font-size: 11px" },
                format: '{0:n2}', // Color %K >= 80 (Red) & %K <= 20 (Green)
            },
            {
                field: 'pivotNote1',
                title: 'PivotNote1',
                width: '18%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                //attributes: { 'class': 'table-cell grid-cell-center' },
                attributes: { "class": "table-cell", style: "text-align: center; font-size: 10px" },
            },
            {
                width: '4%',
                attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                template: kendo.template(TemplateHelpers.getDeleteButtonTemplate('../images', 'id')),
            }
            ]
        }).data('kendoGrid')!;
    }

    private initListValues() {
        const success = ((serverBuySellLists: ServerBuySellLists) => {
            this.serverBuySellLists = serverBuySellLists;
            this.buySellLists = new BuySellLists();
            const activitiesData = [];
            const accountsData = [];

            for (const activity of serverBuySellLists.activities) {
                activitiesData.push({
                    activity: activity
                });
            }
            for (const account of serverBuySellLists.accounts) {
                accountsData.push({
                    account: account
                });
            }

            this.buySellLists.activities = new kendo.data.DataSource({
                data: activitiesData
            });
            this.buySellLists.accounts = new kendo.data.DataSource({
                data: accountsData
            });
        });

        const error = (() => {
            // TODO: what to do on error?
        });

        Ajax.invoke(
            GlobalData.URL_SUFFIX_PERSONAL_REPORTS_STOCKS_TO_BUY_SELL_UPDATE + '/init',
            'GET', null, success, error);
    }

    private symbolEditor(container: any, options: any) {
        const self = StocksToBuySellUpdate.getInstance();

        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.off('blur').on('blur', (element: any) => {
            const gridData = self.grid.dataSource.data();
            const gridDataRow = GridHelper.getCurrentGridDataRow(gridData, element);
            if (gridDataRow) {
                const closeCell = $(element.currentTarget.parentElement.nextSibling);
                const pivotPointCell = closeCell.next();
                const sumQtyOwnCell = pivotPointCell.next().next().next().next().next().next();
                const costPerShareCell = sumQtyOwnCell.next();
                const fullCurrentPctKCell = costPerShareCell.next();
                const pivotNote1Cell = fullCurrentPctKCell.next();

                const urlSuffix = GlobalData.URL_SUFFIX_PERSONAL_REPORTS_STOCKS_TO_BUY_SELL_UPDATE +
                    '/refData?symbol=' + gridDataRow.symbol;

                const success = (data: any) => {
                    if (data) {
                        gridDataRow.close = data.close;
                        closeCell.html(kendo.format("{0:c2}", data.close));
                        gridDataRow.pivotPoint = data.pivotPoint;
                        pivotPointCell.html(kendo.format("{0:c2}", data.pivotPoint));
                        gridDataRow.sumQtyOwn = data.sumQtyOwn;
                        sumQtyOwnCell.html(data.sumQtyOwn);
                        gridDataRow.costPerShare = data.costPerShare;
                        costPerShareCell.html(kendo.format("{0:c2}", data.costPerShare));
                        gridDataRow.fullCurrentPctK = data.fullCurrentPctK;
                        fullCurrentPctKCell.html(kendo.format("{0:c2}", data.fullCurrentPctK));
                        gridDataRow.pivotNote1 = data.pivotNote1;
                        pivotNote1Cell.html(data.pivotNote1);
                    }
                };

                Ajax.invoke(urlSuffix, 'GET', null, success);
            }
        });
    }

    private activityEditor(container: any, options: any) {
        const self = StocksToBuySellUpdate.getInstance();

        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.kendoAutoComplete({
            dataSource: self.buySellLists?.activities,
            dataTextField: 'activity',
            minLength: 1,
            highlightFirst: true,
            suggest: true,
        });
        AutoCompleteHelper.selectText(editor);
    }

    private priceEditor(container: any, options: any) {
        const self = StocksToBuySellUpdate.getInstance();

        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.off('blur').on('blur', (element: any) => {
            const gridData = self.grid.dataSource.data();
            const gridDataRow = GridHelper.getCurrentGridDataRow(gridData, element);
            if (gridDataRow) {
                const priceDiffCell = $(element.currentTarget.parentElement.nextSibling);
                gridDataRow.priceDiff = gridDataRow.price - gridDataRow.close;
                priceDiffCell.html(kendo.format("{0:c2}", gridDataRow.priceDiff));
            }
        });
    }

    private accountEditor(container: any, options: any) {
        const self = StocksToBuySellUpdate.getInstance();

        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.kendoAutoComplete({
            dataSource: self.buySellLists?.accounts,
            dataTextField: 'account',
            minLength: 1,
            highlightFirst: true,
            suggest: true,
        });
        AutoCompleteHelper.selectText(editor);
    }

    private defineDeleteButtonEvents() {
        const deleteButtonClass = TemplateHelpers.getDeleteButtonClass();
        $('.' + deleteButtonClass).off('click').on('click', (event) => {
            const id = event.currentTarget.id.slice(deleteButtonClass.length);
            const symbol = (this.grid.dataSource.get(id) as any).symbol;

            kendo.confirm('Are you sure that you want to delete ' + symbol + '?')
                .then(() => {
                    this.deleteRow(id);
                });
        });
    }

    private deleteRow(id: string) {
        const success = () => {
            this.grid.dataSource.read();
        };

        const error = () => {
            const msg = new Notification();
            msg.error(id + ' could not be deleted');
        };

        Ajax.invoke(GlobalData.URL_SUFFIX_PERSONAL_REPORTS_STOCKS_TO_BUY_SELL_UPDATE +
            '?id=' + id, 'DELETE', null, success, error);
    }
}
